import { Link as TanstackLink, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import { SimpleLogoPageContainer } from '@/components/shared/SimpleLogoPageContainer'
import {
  Button,
  Checkbox,
  Grid,
  Heading,
  Link,
  Stack,
  Text,
} from '@/components/ui'
import { useMutation, useQuerySuspense } from '@/lib/api'
import { createAgreements, getAgreements } from '@/lib/auth'

type AgreementItem = {
  key: keyof Awaited<ReturnType<typeof getAgreements>>
  label: string
  url: string
}
const agreementItems: AgreementItem[] = [
  {
    key: 'agreeTermsOfService',
    label: '利用規約',
    url: `${import.meta.env.VITE_MIROHA_URL}/terms`,
  },
  {
    key: 'agreePrivacyPolicy',
    label: 'プライバシーポリシー',
    url: `${import.meta.env.VITE_MIROHA_URL}/privacy`,
  },
  {
    key: 'agreeExternalTransmissionPolicy',
    label: '外部送信ポリシー',
    url: `${import.meta.env.VITE_MIROHA_URL}/external-transmission-policy`,
  },
]

export const AgreementsPage = () => {
  const { data: agreements } = useQuerySuspense('getAgreements', getAgreements)

  const navigate = useNavigate()

  const { request: submitAgreements } = useMutation(createAgreements, {
    onSuccess: () => {
      navigate({
        to: '/trials',
      })
    },
  })

  const requiredAgreements = agreementItems.filter(
    item => !agreements[item.key],
  )

  const [checkedItemMap, setCheckedItemMap] = useState<Map<string, boolean>>(
    new Map(),
  )
  const [openedItemMap, setOpenedItemMap] = useState<Map<string, boolean>>(
    new Map(),
  )

  return (
    <SimpleLogoPageContainer>
      <Stack boxSize="full" gap="12">
        <Stack gap="2" align="center" w="full">
          <Heading as="h1">同意事項</Heading>
          <Text textAlign="center" overflowWrap="break-word">
            サービスのご利用にあたり、下記の規約をご一読いただき、同意していただく必要があります。
            <br />
            ※リンク先を確認いただくことで同意のチェックボックスが有効になります
          </Text>
        </Stack>
        <Stack gap="4" mx="auto">
          {requiredAgreements.map(item => (
            <Checkbox
              key={item.key}
              checked={checkedItemMap.get(item.key) ?? false}
              readOnly={!openedItemMap.get(item.key)}
              onCheckedChange={e => {
                const checked = !!e.checked
                const newMap = new Map(checkedItemMap)
                newMap.set(item.key, checked)
                setCheckedItemMap(newMap)
              }}
            >
              <Link
                target="_blank"
                href={item.url}
                color="text.primary"
                textDecoration="underline"
                onClick={() => {
                  const newMap = new Map(openedItemMap)
                  newMap.set(item.key, true)
                  setOpenedItemMap(newMap)
                }}
              >
                {item.label}
              </Link>
              に同意します
            </Checkbox>
          ))}
        </Stack>
        <Grid
          gap="6"
          templateColumns="repeat(2, 148px)"
          justifyContent="center"
          w="full"
        >
          <TanstackLink to="/login">
            <Button as="span" variant="outline" w="full">
              キャンセル
            </Button>
          </TanstackLink>
          <Button
            colorScheme="blue"
            disabled={requiredAgreements.some(
              item => !checkedItemMap.get(item.key),
            )}
            onClick={async () => {
              await submitAgreements({
                agreeTermsOfService:
                  checkedItemMap.get('agreeTermsOfService') ?? false,
                agreePrivacyPolicy:
                  checkedItemMap.get('agreePrivacyPolicy') ?? false,
                agreeExternalTransmissionPolicy:
                  checkedItemMap.get('agreeExternalTransmissionPolicy') ??
                  false,
              })
            }}
          >
            次へ
          </Button>
        </Grid>
      </Stack>
    </SimpleLogoPageContainer>
  )
}
